// Tenant Service - Manages multi-tenant functionality for SaaS
export interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  plan: 'free' | 'basic' | 'premium' | 'enterprise';
  status: 'active' | 'suspended' | 'trial' | 'expired';
  createdAt: string;
  updatedAt: string;
  settings: {
    maxBatches: number;
    maxUsers: number;
    dataRetention: number; // days
    features: string[];
  };
  subscription?: {
    startDate: string;
    endDate: string;
    autoRenew: boolean;
    paymentMethod: string;
  };
}

export interface User {
  id: string;
  tenantId: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'manager' | 'worker' | 'viewer';
  permissions: string[];
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

class TenantService {
  private tenants: Tenant[] = [];
  private users: User[] = [];
  private currentTenant: Tenant | null = null;
  private currentUser: User | null = null;

  constructor() {
    this.loadFromStorage();
    this.initializeDefaultTenant();
  }

  // Load data from localStorage
  private loadFromStorage(): void {
    try {
      const tenantsData = localStorage.getItem('tenants');
      if (tenantsData) {
        this.tenants = JSON.parse(tenantsData);
      }

      const usersData = localStorage.getItem('users');
      if (usersData) {
        this.users = JSON.parse(usersData);
      }

      const currentTenantData = localStorage.getItem('currentTenant');
      if (currentTenantData) {
        this.currentTenant = JSON.parse(currentTenantData);
      }

      const currentUserData = localStorage.getItem('currentUser');
      if (currentUserData) {
        this.currentUser = JSON.parse(currentUserData);
      }
    } catch (error) {
      console.error('Error loading tenant data from storage:', error);
    }
  }

  // Save data to localStorage
  private saveToStorage(): void {
    try {
      localStorage.setItem('tenants', JSON.stringify(this.tenants));
      localStorage.setItem('users', JSON.stringify(this.users));
      if (this.currentTenant) {
        localStorage.setItem('currentTenant', JSON.stringify(this.currentTenant));
      }
      if (this.currentUser) {
        localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
      }
    } catch (error) {
      console.error('Error saving tenant data to storage:', error);
    }
  }

  // Initialize default tenant for demo purposes
  private initializeDefaultTenant(): void {
    if (this.tenants.length === 0) {
      const defaultTenant: Tenant = {
        id: 'default_tenant',
        name: 'River Poultry Farm',
        subdomain: 'river-poultry',
        plan: 'premium',
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        settings: {
          maxBatches: 100,
          maxUsers: 10,
          dataRetention: 365,
          features: ['batch_management', 'growth_tracking', 'analytics', 'reports', 'multi_user'],
        },
        subscription: {
          startDate: new Date().toISOString(),
          endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          autoRenew: true,
          paymentMethod: 'credit_card',
        },
      };

      const defaultUser: User = {
        id: 'default_user',
        tenantId: 'default_tenant',
        email: 'admin@riverpoultry.com',
        name: 'Farm Administrator',
        role: 'owner',
        permissions: ['*'], // All permissions
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      this.tenants.push(defaultTenant);
      this.users.push(defaultUser);
      this.currentTenant = defaultTenant;
      this.currentUser = defaultUser;
      this.saveToStorage();
    }
  }

  // Get current tenant
  getCurrentTenant(): Tenant | null {
    return this.currentTenant;
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  // Set current tenant
  setCurrentTenant(tenantId: string): boolean {
    const tenant = this.tenants.find(t => t.id === tenantId);
    if (!tenant) return false;

    this.currentTenant = tenant;
    this.saveToStorage();
    return true;
  }

  // Set current user
  setCurrentUser(userId: string): boolean {
    const user = this.users.find(u => u.id === userId);
    if (!user) return false;

    this.currentUser = user;
    this.saveToStorage();
    return true;
  }

  // Create new tenant
  createTenant(tenantData: Omit<Tenant, 'id' | 'createdAt' | 'updatedAt'>): Tenant {
    const newTenant: Tenant = {
      ...tenantData,
      id: `tenant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.tenants.push(newTenant);
    this.saveToStorage();
    return newTenant;
  }

  // Update tenant
  updateTenant(tenantId: string, updates: Partial<Tenant>): Tenant | null {
    const index = this.tenants.findIndex(t => t.id === tenantId);
    if (index === -1) return null;

    this.tenants[index] = {
      ...this.tenants[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    // Update current tenant if it's the one being updated
    if (this.currentTenant?.id === tenantId) {
      this.currentTenant = this.tenants[index];
    }

    this.saveToStorage();
    return this.tenants[index];
  }

  // Create new user
  createUser(userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): User {
    const newUser: User = {
      ...userData,
      id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.users.push(newUser);
    this.saveToStorage();
    return newUser;
  }

  // Update user
  updateUser(userId: string, updates: Partial<User>): User | null {
    const index = this.users.findIndex(u => u.id === userId);
    if (index === -1) return null;

    this.users[index] = {
      ...this.users[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    // Update current user if it's the one being updated
    if (this.currentUser?.id === userId) {
      this.currentUser = this.users[index];
    }

    this.saveToStorage();
    return this.users[index];
  }

  // Get users for current tenant
  getTenantUsers(): User[] {
    if (!this.currentTenant) return [];
    return this.users.filter(u => u.tenantId === this.currentTenant!.id);
  }

  // Get all tenants (for super admin)
  getAllTenants(): Tenant[] {
    return [...this.tenants];
  }

  // Get all users (for super admin)
  getAllUsers(): User[] {
    return [...this.users];
  }

  // Check if user has permission
  hasPermission(permission: string): boolean {
    if (!this.currentUser) return false;
    if (this.currentUser.permissions.includes('*')) return true;
    return this.currentUser.permissions.includes(permission);
  }

  // Check if user has role
  hasRole(role: string): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.role === role;
  }

  // Get storage key for tenant-specific data
  getStorageKey(key: string): string {
    if (!this.currentTenant) return key;
    return `${this.currentTenant.id}_${key}`;
  }

  // Get plan limits
  getPlanLimits(): { maxBatches: number; maxUsers: number; features: string[] } {
    if (!this.currentTenant) {
      return { maxBatches: 5, maxUsers: 1, features: ['basic'] };
    }
    return {
      maxBatches: this.currentTenant.settings.maxBatches,
      maxUsers: this.currentTenant.settings.maxUsers,
      features: this.currentTenant.settings.features,
    };
  }

  // Check if feature is available
  isFeatureAvailable(feature: string): boolean {
    const limits = this.getPlanLimits();
    return limits.features.includes(feature);
  }

  // Get tenant statistics
  getTenantStatistics(): {
    totalUsers: number;
    activeUsers: number;
    plan: string;
    status: string;
    daysRemaining: number;
  } {
    if (!this.currentTenant) {
      return { totalUsers: 0, activeUsers: 0, plan: 'free', status: 'inactive', daysRemaining: 0 };
    }

    const tenantUsers = this.getTenantUsers();
    const activeUsers = tenantUsers.filter(u => u.isActive).length;
    
    let daysRemaining = 0;
    if (this.currentTenant.subscription) {
      const endDate = new Date(this.currentTenant.subscription.endDate);
      const today = new Date();
      daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
    }

    return {
      totalUsers: tenantUsers.length,
      activeUsers,
      plan: this.currentTenant.plan,
      status: this.currentTenant.status,
      daysRemaining,
    };
  }

  // Delete tenant
  deleteTenant(tenantId: string): boolean {
    const index = this.tenants.findIndex(t => t.id === tenantId);
    if (index === -1) return false;

    this.tenants.splice(index, 1);
    // Also remove all users for this tenant
    this.users = this.users.filter(u => u.tenantId !== tenantId);
    
    // Clear current tenant if it was deleted
    if (this.currentTenant?.id === tenantId) {
      this.currentTenant = null;
      this.currentUser = null;
    }

    this.saveToStorage();
    return true;
  }

  // Delete user
  deleteUser(userId: string): boolean {
    const index = this.users.findIndex(u => u.id === userId);
    if (index === -1) return false;

    this.users.splice(index, 1);
    
    // Clear current user if it was deleted
    if (this.currentUser?.id === userId) {
      this.currentUser = null;
    }

    this.saveToStorage();
    return true;
  }
}

// Export singleton instance
export const tenantService = new TenantService();
